
import { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { CuotaItem, VentaCuotasResponse } from '@/lib/adelantos-service';
import { useAuth } from '@/contexts/AuthContext';

interface CartItem {
  ventaId: string;
  fechaVenta: string;
  adquirente: string;
  selectedCuotaCount: number;
  totalBruto: number;
  totalRecibiras: number;
  totalComision: number;
  tye: number;
}

export interface VentaInfo {
  fechaVenta: string;
  adquirente: string;
  cuotas: CuotaItem[];
  comisionFlux: number | null;
  tye: number | null;
  recibiras: number | null;
  /** Tasas TYE para recalcular en cliente cuando el subset seleccionado != total. */
  stampTaxRate?: number;
  stampTaxCap?: number;
}

interface AdelantoCartContextType {
  selections: Record<string, string[]>;
  ventaRegistry: Record<string, VentaInfo>;
  cartItems: CartItem[];
  totalBruto: number;
  totalComision: number;
  totalTye: number;
  totalRecibiras: number;
  totalCuotaCount: number;
  montoMinimo: number;
  setMontoMinimo: (value: number) => void;
  registerVenta: (ventaId: string, info: VentaInfo) => void;
  updateVentaCuotas: (ventaId: string, cuotasData: VentaCuotasResponse) => void;
  toggleCuota: (ventaId: string, cuotaId: string) => void;
  toggleAllCuotas: (ventaId: string, freshCuotaIds?: string[]) => void;
  removeVenta: (ventaId: string) => void;
  clearCart: () => void;
  isAllSelected: (ventaId: string) => boolean;
  isIndeterminate: (ventaId: string) => boolean;
  isCuotaSelected: (ventaId: string, cuotaId: string) => boolean;
  registerOnSolicitudConfirmada: (cb: () => void) => () => void;
  notifySolicitudConfirmada: () => void;
}

const AdelantoCartContext = createContext<AdelantoCartContextType | null>(null);

/**
 * Recalcula el TYE para el subset de cuotas seleccionadas usando la misma
 * fórmula del backend (`calculate-advance-commission.ts`):
 *   baseTye = sum(payment_amount where advanceDays > 0)
 *   tye     = round(MIN(baseTye × N × rate, baseTye × cap))   donde N = subset.length
 *
 * El backend manda `stampTaxRate` y `stampTaxCap` en la respuesta de
 * `getSaleInstallments` para evitar la divergencia que ocurría cuando el
 * frontend usaba el TYE pre-calculado para todas las cuotas.
 *
 * `comisionFlux` viene como negativo desde el backend (representación contable
 * en el carrito), así que el signo del TYE lo replicamos: negativo cuando hay
 * cargo, 0 cuando no.
 *
 * Si las tasas no llegaron (legacy o endpoint viejo), fallback al TYE
 * pre-calculado.
 */
function computeSubsetTye(
  selectedCuotas: CuotaItem[],
  stampTaxRate: number | undefined,
  stampTaxCap: number | undefined,
  fallbackTye: number | null,
): number {
  if (selectedCuotas.length === 0) return 0;
  if (stampTaxRate === undefined || stampTaxCap === undefined) {
    return fallbackTye ?? 0;
  }
  const baseTye = selectedCuotas.filter((c) => c.diasAdelanto > 0).reduce((sum, c) => sum + c.montoOriginal, 0);
  if (baseTye <= 0) return 0;
  const n = selectedCuotas.length;
  const tye = Math.round(Math.min(baseTye * n * stampTaxRate, baseTye * stampTaxCap));
  // Conservar el signo negativo que usa el resto del carrito para "cargos al usuario".
  return -tye;
}

export function AdelantoCartProvider({ children }: { children: ReactNode }) {
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [ventaRegistry, setVentaRegistry] = useState<Record<string, VentaInfo>>({});
  const [montoMinimo, setMontoMinimo] = useState<number>(0);

  const { user } = useAuth();
  const prevUserKeyRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const currentKey = user ? (user.id ?? user.email) : null;
    if (prevUserKeyRef.current !== undefined && prevUserKeyRef.current !== currentKey) {
      setSelections({});
      setVentaRegistry({});
      setMontoMinimo(0);
    }
    prevUserKeyRef.current = currentKey;
  }, [user]);

  const onConfirmadaCallbacksRef = useRef<Set<() => void>>(new Set());

  const registerOnSolicitudConfirmada = useCallback((cb: () => void) => {
    onConfirmadaCallbacksRef.current.add(cb);
    return () => { onConfirmadaCallbacksRef.current.delete(cb); };
  }, []);

  const notifySolicitudConfirmada = useCallback(() => {
    onConfirmadaCallbacksRef.current.forEach((cb) => cb());
  }, []);

  const registerVenta = useCallback((ventaId: string, info: VentaInfo) => {
    setVentaRegistry((prev) => {
      if (prev[ventaId]) return prev;
      return { ...prev, [ventaId]: info };
    });
  }, []);

  const updateVentaCuotas = useCallback((ventaId: string, cuotasData: VentaCuotasResponse) => {
    setVentaRegistry((prev) => {
      const existing = prev[ventaId];
      if (!existing) return prev;
      return {
        ...prev,
        [ventaId]: {
          ...existing,
          cuotas: cuotasData.cuotas,
          comisionFlux: cuotasData.comisionFlux,
          tye: cuotasData.tye,
          recibiras: cuotasData.recibiras,
          stampTaxRate: cuotasData.stampTaxRate,
          stampTaxCap: cuotasData.stampTaxCap,
        },
      };
    });
  }, []);

  const ventaRegistryRef = useRef(ventaRegistry);
  ventaRegistryRef.current = ventaRegistry;

  const toggleCuota = useCallback(
    (ventaId: string, cuotaId: string) => {
      const venta = ventaRegistryRef.current[ventaId];
      setSelections((prev) => {
        const current = prev[ventaId] ?? [];
        const isSelected = current.includes(cuotaId);

        let updated: string[];

        if (venta && venta.cuotas.length > 0) {
          const allIds = venta.cuotas.map((c) => c.id);
          const cuotaIndex = allIds.indexOf(cuotaId);

          if (isSelected) {
            updated = current.filter((id) => {
              const idx = allIds.indexOf(id);
              return idx < cuotaIndex;
            });
          } else {
            const idsUpToAndIncluding = allIds.slice(0, cuotaIndex + 1);
            const merged = new Set([...current, ...idsUpToAndIncluding]);
            updated = allIds.filter((id) => merged.has(id));
          }
        } else {
          updated = isSelected ? current.filter((id) => id !== cuotaId) : [...current, cuotaId];
        }

        if (updated.length === 0) {
          const { [ventaId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [ventaId]: updated };
      });
    },
    [],
  );

  const toggleAllCuotas = useCallback(
    (ventaId: string, freshCuotaIds?: string[]) => {
      const venta = ventaRegistryRef.current[ventaId];
      if (!venta) return;

      setSelections((prev) => {
        const allCuotaIds = freshCuotaIds ?? venta.cuotas.map((c) => c.id);
        const current = prev[ventaId] ?? [];
        const allSelected =
          allCuotaIds.length > 0 && allCuotaIds.every((id) => current.includes(id));

        if (allSelected) {
          const { [ventaId]: _, ...rest } = prev;
          return rest;
        }

        if (allCuotaIds.length > 0) {
          return { ...prev, [ventaId]: allCuotaIds };
        }

        const alreadySelected = !!prev[ventaId];
        if (alreadySelected) {
          const { [ventaId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [ventaId]: ['__parent__'] };
      });
    },
    [],
  );

  const removeVenta = useCallback((ventaId: string) => {
    setSelections((prev) => {
      const { [ventaId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearCart = useCallback(() => setSelections({}), []);

  const isAllSelected = useCallback(
    (ventaId: string) => {
      const venta = ventaRegistry[ventaId];
      if (!venta) return false;
      if (venta.cuotas.length === 0) return !!(selections[ventaId]?.includes('__parent__'));
      const current = selections[ventaId] ?? [];
      return venta.cuotas.every((c) => current.includes(c.id));
    },
    [selections, ventaRegistry],
  );

  const isIndeterminate = useCallback(
    (ventaId: string) => {
      const venta = ventaRegistry[ventaId];
      if (!venta || venta.cuotas.length === 0) return false;
      const current = selections[ventaId] ?? [];
      const selectedCount = venta.cuotas.filter((c) => current.includes(c.id)).length;
      return selectedCount > 0 && selectedCount < venta.cuotas.length;
    },
    [selections, ventaRegistry],
  );

  const isCuotaSelected = useCallback(
    (ventaId: string, cuotaId: string) => {
      return (selections[ventaId] ?? []).includes(cuotaId);
    },
    [selections],
  );

  const cartItems: CartItem[] = Object.entries(selections)
    .map(([ventaId, cuotaIds]) => {
      const venta = ventaRegistry[ventaId];
      if (!venta) return null;

      const selectedCuotas = venta.cuotas.filter((c) => cuotaIds.includes(c.id));
      const totalBruto = selectedCuotas.reduce((sum, c) => sum + c.montoOriginal, 0);
      const totalRecibiras = selectedCuotas.reduce((sum, c) => sum + c.recibiras, 0);
      const totalComision = selectedCuotas.reduce((sum, c) => sum + c.comisionFlux, 0);

      // TYE depende del subset (base × N × rate, capped por base × cap). Si el
      // backend nos entregó las tasas las usamos para recalcular acá; si no
      // (legacy o falla del endpoint) caemos al pre-calculado para todo el set.
      const tye = computeSubsetTye(selectedCuotas, venta.stampTaxRate, venta.stampTaxCap, venta.tye);

      return {
        ventaId,
        fechaVenta: venta.fechaVenta,
        adquirente: venta.adquirente,
        selectedCuotaCount: cuotaIds.filter((id) => id !== '__parent__').length || cuotaIds.length,
        totalBruto,
        totalRecibiras,
        totalComision,
        tye,
      } as CartItem;
    })
    .filter(Boolean) as CartItem[];

  const totalBruto = cartItems.reduce((sum, item) => sum + item.totalRecibiras - item.totalComision, 0);
  const totalComision = cartItems.reduce((sum, item) => sum + item.totalComision, 0);
  const totalTye = cartItems.reduce((sum, item) => sum + item.tye, 0);
  const totalRecibiras = cartItems.reduce((sum, item) => sum + item.totalRecibiras, 0);
  const totalCuotaCount = cartItems.reduce((sum, item) => sum + item.selectedCuotaCount, 0);

  return (
    <AdelantoCartContext.Provider
      value={{
        selections,
        ventaRegistry,
        cartItems,
        totalBruto,
        totalComision,
        totalTye,
        totalRecibiras,
        totalCuotaCount,
        montoMinimo,
        setMontoMinimo,
        registerVenta,
        updateVentaCuotas,
        toggleCuota,
        toggleAllCuotas,
        removeVenta,
        clearCart,
        isAllSelected,
        isIndeterminate,
        isCuotaSelected,
        registerOnSolicitudConfirmada,
        notifySolicitudConfirmada,
      }}
    >
      {children}
    </AdelantoCartContext.Provider>
  );
}

export function useAdelantoCart() {
  const ctx = useContext(AdelantoCartContext);
  if (!ctx) throw new Error('useAdelantoCart must be used within AdelantoCartProvider');
  return ctx;
}
